import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, TrendingDown, ChevronRight } from 'lucide-react'
import {
  fetchHargaLatest,
  formatRupiah,
  formatTanggalPanjang,
  komoditasOf,
  rataNasional,
  ekstremHarga,
  jumlahProvinsiLapor,
  KOMODITAS_LABEL,
} from '@/services/hargaKomoditas'
import type { HargaKomoditasData, KomoditasMeta } from '@/types/harga-komoditas'

function WidgetSkeleton() {
  return (
    <div className="@container rounded-2xl border bg-card shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 bg-muted rounded animate-pulse w-40" />
        <div className="h-3.5 bg-muted rounded animate-pulse w-16" />
      </div>
      <div className="grid grid-cols-2 @2xl:grid-cols-4 gap-2.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] bg-muted/60 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  )
}

function WidgetItem({
  meta,
  nilai,
}: {
  meta: KomoditasMeta
  nilai: { harga: number; delta: number; persen: number } | null
}) {
  const label = KOMODITAS_LABEL[meta.variant_id]
  const naik = (nilai?.delta ?? 0) > 0
  const ada = nilai && nilai.harga > 0

  return (
    <div className="bg-secondary/40 dark:bg-secondary/20 rounded-xl p-3">
      {/* Tanpa ikon per komoditas — nama dapat lebar penuh dan muat satu baris. */}
      <p className="text-[11px] font-semibold text-muted-foreground mb-1 leading-tight whitespace-nowrap">
        {label?.pendek ?? meta.nama}
      </p>
      {ada ? (
        <>
          <p className="text-[15px] font-black text-foreground leading-tight tabular-nums whitespace-nowrap">
            {formatRupiah(nilai.harga)}
          </p>
          <p className="text-[9px] text-muted-foreground">per {meta.satuan}</p>
          {/* Warna dipilih untuk kontras, bukan kecerahan: teks 10px BUKAN
              "large text" menurut WCAG, jadi butuh rasio 4.5:1. Di atas latar
              mint muda ini, red-500 hanya 3,3:1 dan green-600 3,0:1 — keduanya
              gagal. red-700 (5,7:1) dan green-800 (6,3:1) lolos, sementara
              di mode gelap justru versi terang yang dipakai. */}
          <div
            className={`flex items-center gap-0.5 mt-0.5 text-[10px] font-bold ${
              nilai.delta === 0
                ? 'text-muted-foreground'
                : naik
                  ? 'text-red-700 dark:text-red-400'
                  : 'text-green-800 dark:text-green-400'
            }`}
          >
            {nilai.delta !== 0 && (naik ? <TrendingUp size={10} /> : <TrendingDown size={10} />)}
            <span>{nilai.delta === 0 ? 'Stabil' : `${naik ? '+' : ''}${nilai.persen.toFixed(1)}%`}</span>
          </div>
        </>
      ) : (
        <p className="text-base font-black text-muted-foreground">-</p>
      )}
    </div>
  )
}

/**
 * Sebaran harga satu komoditas antar daerah.
 *
 * Angka rata-rata saja menyembunyikan hal yang paling dicari peternak: selisih
 * antar daerah. Ayam dan telur dipisah karena pasarnya bergerak sendiri-sendiri
 * — daerah termurah untuk ayam belum tentu termurah untuk telur.
 */
function SorotanEkstrem({
  judul,
  ekstrem,
  nProvinsi,
}: {
  judul: string
  ekstrem: { termurah: { nama: string; harga: number }; termahal: { nama: string; harga: number } }
  nProvinsi: number
}) {
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground/80 mb-1">
        {judul} · {nProvinsi} provinsi
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">Termurah</p>
          <p className="text-xs font-black text-green-800 dark:text-green-400 tabular-nums">
            {formatRupiah(ekstrem.termurah.harga)}
          </p>
          <p className="text-[10px] text-muted-foreground leading-tight">{ekstrem.termurah.nama}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">Termahal</p>
          <p className="text-xs font-black text-red-700 dark:text-red-400 tabular-nums">
            {formatRupiah(ekstrem.termahal.harga)}
          </p>
          <p className="text-[10px] text-muted-foreground leading-tight">{ekstrem.termahal.nama}</p>
        </div>
      </div>
    </div>
  )
}

export function HargaKomoditasWidget() {
  const [data, setData] = useState<HargaKomoditasData | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchHargaLatest()
      .then((result) => { if (!cancelled) setData(result) })
      .catch(() => { if (!cancelled) setHasError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  if (loading) return <WidgetSkeleton />

  if (hasError || !data) {
    return (
      <div className="@container rounded-2xl border bg-card shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-black">Harga Ayam &amp; Telur Hari Ini</h2>
          <Link to="/harga-komoditas" className="text-xs text-primary font-bold flex items-center gap-0.5 active:opacity-70">
            Lihat Semua <ChevronRight size={13} />
          </Link>
        </div>
        <p className="text-xs text-muted-foreground py-2 text-center">Data tidak tersedia</p>
      </div>
    )
  }

  const komoditas = komoditasOf(data)
  // Sorotan pakai dua komoditas yang paling banyak dilaporkan sekaligus paling
  // sering dicari peternak: Daging Ayam Ras (27) dan Telur Ayam Ras (25).
  const sorotan = [
    { judul: 'Ayam ras', variantId: 27 },
    { judul: 'Telur ras', variantId: 25 },
  ]
    .map((s) => ({
      ...s,
      ekstrem: ekstremHarga(data, s.variantId),
      nProvinsi: jumlahProvinsiLapor(data, s.variantId),
    }))
    .filter((s) => s.ekstrem !== null)

  return (
    <div className="@container rounded-2xl border bg-card shadow-sm p-4">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-black leading-tight">Harga Ayam &amp; Telur</h2>
          <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
            Rata-rata nasional · {formatTanggalPanjang(data.tanggal)}
          </p>
        </div>
        <Link
          to="/harga-komoditas"
          className="text-xs text-primary font-bold flex items-center gap-0.5 active:opacity-70 shrink-0"
        >
          Lihat Semua <ChevronRight size={13} />
        </Link>
      </div>

      {/* Grid 2×2 di ponsel: keempat komoditas terlihat sekaligus, tanpa digeser. */}
      <div className="grid grid-cols-2 @2xl:grid-cols-4 gap-2.5">
        {komoditas.map((meta) => (
          <WidgetItem key={meta.variant_id} meta={meta} nilai={rataNasional(data, meta.variant_id)} />
        ))}
      </div>

      {/* Sorotan: angka mentah saja kurang berguna — tunjukkan sebaran antar daerah. */}
      {sorotan.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/60 flex flex-col gap-3">
          {sorotan.map((s) => (
            <SorotanEkstrem
              key={s.variantId}
              judul={s.judul}
              ekstrem={s.ekstrem!}
              nProvinsi={s.nProvinsi}
            />
          ))}
        </div>
      )}
    </div>
  )
}
